import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Package,
    Truck,
    FileText,
    CheckCircle,
    CheckCircle2,
    Clock,
    AlertTriangle,
    Loader2,
    Download,
    Send,
    ChevronDown,
    ChevronUp,
    MapPin,
    Phone,
    RefreshCw,
    ScanBarcode,
    RotateCcw,
    XCircle,
    Ban,
    Upload,
    ExternalLink,
    Replace,
    Trash2,
    Search,
    CreditCard,
    Wallet
} from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Sound alerts
const playErrorSound = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.value = 400; // Low frequency for error
    oscillator.type = 'square';
    gainNode.gain.value = 0.3;
    oscillator.start();
    setTimeout(() => {
        oscillator.stop();
        audioContext.close();
    }, 300);
};

const playSuccessSound = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.value = 800; // Higher frequency for success
    oscillator.type = 'sine';
    gainNode.gain.value = 0.2;
    oscillator.start();
    setTimeout(() => {
        oscillator.stop();
        audioContext.close();
    }, 150);
};

const AdminOrderPreparation = () => {
    const { token } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedOrder, setExpandedOrder] = useState(null);
    const [processingAction, setProcessingAction] = useState(null);
    const [scanInput, setScanInput] = useState('');
    const [verificationStatus, setVerificationStatus] = useState({});
    const scanInputRef = useRef(null);
    
    // Cancel modal state
    const [cancelModal, setCancelModal] = useState({ open: false, order: null });
    const [cancellationReason, setCancellationReason] = useState('');
    
    // Shipping modal state
    const [shipModal, setShipModal] = useState({ open: false, order: null });
    const [manualShipment, setManualShipment] = useState({
        carrier: '',
        trackingNumber: '',
        hasTracking: true
    });
    const [uploadingLabel, setUploadingLabel] = useState(false);

    // Replacement modal state
    const [replaceModal, setReplaceModal] = useState({ 
        open: false, 
        order: null, 
        item: null 
    });
    const [replacementSuggestions, setReplacementSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [manualImei, setManualImei] = useState('');
    const [selectedReason, setSelectedReason] = useState('');
    const [reasonDetails, setReasonDetails] = useState('');
    const [replacingItem, setReplacingItem] = useState(false);

    // Partial shipment state
    const [selectedForShipment, setSelectedForShipment] = useState({});  // { orderId: [imei1, imei2] }
    const [partialShipModal, setPartialShipModal] = useState({ open: false, order: null });
    const [partialShipmentData, setPartialShipmentData] = useState({
        carrier: '',
        trackingNumber: '',
        hasTracking: true
    });
    const [creatingPartialShipment, setCreatingPartialShipment] = useState(false);
    const [orderShipments, setOrderShipments] = useState({});  // { orderId: shipments[] }
    
    // Bulk removal state
    const [bulkRemovalModal, setBulkRemovalModal] = useState({ open: false, order: null });
    const [bulkRemovalReason, setBulkRemovalReason] = useState('');
    const [bulkRemovalDetails, setBulkRemovalDetails] = useState('');
    const [removingBulk, setRemovingBulk] = useState(false);
    const [skipWalletCredit, setSkipWalletCredit] = useState(false);

    const REMOVAL_REASONS = [
        { value: 'stock_unavailable', label: 'Stock indisponible' },
        { value: 'grade_incorrect', label: 'Grade incorrect' },
        { value: 'defect_found', label: 'Défaut constaté' },
        { value: 'wrong_product', label: 'Mauvais produit' },
        { value: 'other', label: 'Autre raison' }
    ];

    const MANUAL_CARRIERS = [
        { value: 'chronopost', label: 'Chronopost' },
        { value: 'ups', label: 'UPS' },
        { value: 'dhl', label: 'DHL' },
        { value: 'fedex', label: 'FedEx' },
        { value: 'laposte', label: 'La Poste' },
        { value: 'colissimo', label: 'Colissimo' },
        { value: 'other', label: 'Autre' }
    ];

    const fetchOrders = useCallback(async () => {
        try {
            const response = await axios.get(
                `${API_URL}/admin/orders?status=pending,confirmed,preparing,partially_shipped,shipped`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Filter orders that need preparation (confirmed/preparing/partially_shipped) or tracking (shipped)
            const preparationOrders = (response.data.orders || []).filter(o => 
                ['confirmed', 'preparing', 'partially_shipped', 'shipped'].includes(o.status)
            );
            setOrders(preparationOrders);
        } catch (error) {
            console.error('Error fetching orders:', error);
            toast.error('Erreur lors du chargement des commandes');
        } finally {
            setLoading(false);
        }
    }, [token]);

    // Fetch verification status for an order
    const fetchVerificationStatus = useCallback(async (orderId) => {
        try {
            const response = await axios.get(
                `${API_URL}/admin/orders/${orderId}/verification-status`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setVerificationStatus(prev => ({
                ...prev,
                [orderId]: response.data
            }));
        } catch (error) {
            console.error('Error fetching verification status:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchOrders();
        // Auto-refresh every 30 seconds
        const interval = setInterval(fetchOrders, 30000);
        return () => clearInterval(interval);
    }, [fetchOrders]);

    // Load verification status when order is expanded
    useEffect(() => {
        if (expandedOrder) {
            fetchVerificationStatus(expandedOrder);
            // Focus on scan input when order is expanded
            setTimeout(() => {
                if (scanInputRef.current) {
                    scanInputRef.current.focus();
                }
            }, 100);
        }
    }, [expandedOrder, fetchVerificationStatus]);

    // Handle IMEI scan
    const handleScanIMEI = async (orderId, imei) => {
        if (!imei.trim()) return;
        
        try {
            const response = await axios.post(
                `${API_URL}/admin/orders/${orderId}/verify-imei`,
                { imei: imei.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.data.success) {
                playSuccessSound();
                toast.success(
                    <div>
                        <strong>✓ IMEI vérifié</strong>
                        <div className="text-sm">{response.data.item?.brand} {response.data.item?.model}</div>
                        <div className="text-xs text-green-600">{response.data.verified_count}/{response.data.total_count} articles vérifiés</div>
                    </div>
                );
                // Refresh verification status
                fetchVerificationStatus(orderId);
            } else {
                playErrorSound();
                if (response.data.error === 'already_scanned') {
                    toast.error(
                        <div>
                            <strong>⚠️ Déjà scanné</strong>
                            <div className="text-sm">{response.data.message}</div>
                        </div>
                    );
                } else {
                    toast.error(
                        <div>
                            <strong>❌ IMEI incorrect</strong>
                            <div className="text-sm">{response.data.message}</div>
                        </div>
                    );
                }
            }
        } catch (error) {
            playErrorSound();
            toast.error('Erreur lors de la vérification');
        }
        
        setScanInput('');
        // Re-focus on input for next scan
        if (scanInputRef.current) {
            scanInputRef.current.focus();
        }
    };

    // Reset all verifications
    const handleResetVerification = async (orderId) => {
        if (!window.confirm('Réinitialiser toutes les vérifications pour cette commande ?')) return;
        
        try {
            await axios.post(
                `${API_URL}/admin/orders/${orderId}/reset-verification`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Vérifications réinitialisées');
            fetchVerificationStatus(orderId);
        } catch (error) {
            toast.error('Erreur lors de la réinitialisation');
        }
    };

    // Cancel order with reason
    const handleCancelOrder = async () => {
        if (!cancelModal.order) return;
        if (!cancellationReason.trim()) {
            toast.error('Veuillez indiquer un motif d\'annulation');
            return;
        }
        
        setProcessingAction(`cancel-${cancelModal.order.id}`);
        try {
            await axios.put(
                `${API_URL}/admin/orders/${cancelModal.order.id}/status`,
                { 
                    status: 'cancelled',
                    cancellation_reason: cancellationReason.trim()
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Commande annulée');
            setCancelModal({ open: false, order: null });
            setCancellationReason('');
            fetchOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
        } finally {
            setProcessingAction(null);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { label: 'En attente paiement', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
            confirmed: { label: 'À préparer', color: 'bg-blue-100 text-blue-800', icon: Package },
            preparing: { label: 'En préparation', color: 'bg-purple-100 text-purple-800', icon: Package },
            shipped: { label: 'Expédiée', color: 'bg-green-100 text-green-800', icon: Truck },
            delivered: { label: 'Livrée', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle }
        };
        const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800', icon: Package };
        const Icon = config.icon;
        return (
            <Badge className={`${config.color} flex items-center gap-1`}>
                <Icon className="w-3 h-3" />
                {config.label}
            </Badge>
        );
    };

    // Generate shipping label via Boxtal
    const handleGenerateLabel = async (order) => {
        if (!order.shipping_method) {
            toast.error('Mode de livraison non défini');
            return;
        }
        
        setProcessingAction(`label-${order.id}`);
        try {
            const response = await axios.post(
                `${API_URL}/admin/orders/${order.id}/generate-shipment`,
                { carrier_code: order.shipping_method },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Bon de transport généré !');
            
            // Refresh orders and update modal with fresh data
            const ordersResponse = await axios.get(
                `${API_URL}/admin/orders?status=pending,confirmed,preparing,partially_shipped,shipped`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const allOrders = ordersResponse.data.orders || [];
            const preparationOrders = allOrders.filter(o => 
                ['pending', 'confirmed', 'paid', 'preparing', 'partially_shipped'].includes(o.status)
            );
            setOrders(preparationOrders);
            
            // Update the modal with fresh order data
            const updatedOrder = allOrders.find(o => o.id === order.id);
            if (updatedOrder && shipModal.open) {
                setShipModal({ open: true, order: updatedOrder });
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur génération label');
        } finally {
            setProcessingAction(null);
        }
    };

    // Download shipping label PDF
    const handleDownloadLabel = async (order) => {
        setProcessingAction(`download-${order.id}`);
        try {
            const response = await axios.get(
                `${API_URL}/admin/orders/${order.id}/label`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );
            
            // Check if error response
            if (response.data.type === 'application/json') {
                const text = await response.data.text();
                const error = JSON.parse(text);
                toast.error(error.detail || 'Erreur téléchargement');
                return;
            }
            
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `bordereau_${order.shipment?.reference || order.id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Bon de transport téléchargé');
        } catch (error) {
            const reference = order.shipment?.reference || order.shipment?.emc_reference;
            if (error.response?.data instanceof Blob) {
                const text = await error.response.data.text();
                try {
                    const errorData = JSON.parse(text);
                    // Show error with Boxtal link
                    toast.error(
                        <div>
                            <p>{errorData.detail || 'Erreur téléchargement'}</p>
                            {reference && (
                                <a 
                                    href="https://www.envoimoinscher.com" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-400 underline text-sm"
                                >
                                    → Ouvrir Boxtal (Réf: {reference})
                                </a>
                            )}
                        </div>,
                        { duration: 10000 }
                    );
                } catch {
                    toast.error('Erreur téléchargement - Récupérez le bordereau sur envoimoinscher.com');
                }
            } else {
                toast.error('Erreur téléchargement - Récupérez le bordereau sur envoimoinscher.com');
            }
        } finally {
            setProcessingAction(null);
        }
    };

    // Open Boxtal to download label manually
    const handleOpenBoxtal = (order) => {
        const reference = order.shipment?.reference || order.shipment?.emc_reference;
        if (reference) {
            // Link directly to Boxtal shipments page (better than homepage)
            window.open('https://www.boxtal.com/fr/fr/mon-espace/mes-envois', '_blank');
            toast.info(`Référence Boxtal: ${reference}`, { duration: 15000 });
        } else {
            toast.error('Pas de référence Boxtal disponible');
        }
    };

    // Download packing slip / order form
    const handleDownloadPackingSlip = async (order) => {
        setProcessingAction(`packing-${order.id}`);
        try {
            const response = await axios.get(
                `${API_URL}/admin/orders/${order.id}/packing-slip`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );
            
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `bon_commande_${order.id.slice(0,8)}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Bon de commande téléchargé');
        } catch (error) {
            toast.error('Erreur génération bon de commande');
        } finally {
            setProcessingAction(null);
        }
    };

    // Mark as shipped and generate invoice
    const handleMarkShipped = async (order, useBoxtal = true) => {
        setProcessingAction(`ship-${order.id}`);
        try {
            if (useBoxtal) {
                // Use existing Boxtal shipment - endpoint is PUT
                await axios.put(
                    `${API_URL}/admin/orders/${order.id}/status`,
                    { status: 'shipped', tracking_number: order.tracking_number },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Commande marquée comme expédiée !');
            }
            setShipModal({ open: false, order: null });
            fetchOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'expédition');
        } finally {
            setProcessingAction(null);
        }
    };

    // Manual shipment
    const handleManualShipment = async (order) => {
        if (!manualShipment.carrier) {
            toast.error('Veuillez sélectionner un transporteur');
            return;
        }
        
        setProcessingAction(`ship-${order.id}`);
        try {
            await axios.post(
                `${API_URL}/admin/orders/${order.id}/manual-shipment`,
                {
                    carrier: manualShipment.carrier,
                    tracking_number: manualShipment.trackingNumber || null,
                    has_tracking: manualShipment.hasTracking
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(`Commande expédiée via ${MANUAL_CARRIERS.find(c => c.value === manualShipment.carrier)?.label}`);
            setShipModal({ open: false, order: null });
            setManualShipment({ carrier: '', trackingNumber: '', hasTracking: true });
            fetchOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'expédition');
        } finally {
            setProcessingAction(null);
        }
    };

    // Upload shipping label
    const handleUploadLabel = async (orderId, file) => {
        if (!file) return;
        
        setUploadingLabel(true);
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            await axios.post(
                `${API_URL}/admin/orders/${orderId}/upload-shipping-label`,
                formData,
                { 
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    } 
                }
            );
            toast.success('Bon de transport uploadé');
            fetchOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
        } finally {
            setUploadingLabel(false);
        }
    };

    // Download invoice
    const handleDownloadInvoice = async (order) => {
        setProcessingAction(`invoice-${order.id}`);
        try {
            const response = await axios.get(
                `${API_URL}/admin/orders/${order.id}/invoice`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );
            
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `facture_${order.id.slice(0,8)}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Facture téléchargée');
        } catch (error) {
            toast.error('Erreur téléchargement facture');
        } finally {
            setProcessingAction(null);
        }
    };

    const handleGenerateInvoice = async (order) => {
        setProcessingAction(`gen-invoice-${order.id}`);
        try {
            const response = await axios.post(
                `${API_URL}/admin/orders/${order.id}/generate-invoice`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Facture générée avec succès');
            fetchOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur génération facture');
        } finally {
            setProcessingAction(null);
        }
    };

    // Open replacement modal for an item
    const handleOpenReplaceModal = async (order, item) => {
        setReplaceModal({ open: true, order, item });
        setSelectedReason('');
        setReasonDetails('');
        setManualImei('');
        setReplacementSuggestions([]);
        
        // Fetch replacement suggestions
        setLoadingSuggestions(true);
        try {
            const response = await axios.get(
                `${API_URL}/admin/orders/${order.id}/replacement-suggestions/${item.imei}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setReplacementSuggestions(response.data.suggestions || []);
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            toast.error('Erreur lors de la recherche de remplacements');
        } finally {
            setLoadingSuggestions(false);
        }
    };

    // Replace item with a suggestion
    const handleReplaceItem = async (replacementImei) => {
        if (!selectedReason) {
            toast.error('Veuillez sélectionner une raison');
            return;
        }
        
        setReplacingItem(true);
        try {
            const response = await axios.post(
                `${API_URL}/admin/orders/${replaceModal.order.id}/replace-item`,
                {
                    original_imei: replaceModal.item.imei,
                    replacement_imei: replacementImei,
                    reason: selectedReason,
                    reason_details: reasonDetails || null
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(
                <div>
                    <strong>Article remplacé</strong>
                    {response.data.price_difference > 0 && (
                        <div className="text-sm text-green-600">
                            Avoir de {response.data.price_difference}€ crédité au wallet
                        </div>
                    )}
                </div>
            );
            
            setReplaceModal({ open: false, order: null, item: null });
            fetchOrders();
            if (replaceModal.order?.id) {
                fetchVerificationStatus(replaceModal.order.id);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du remplacement');
        } finally {
            setReplacingItem(false);
        }
    };

    // Replace with manual IMEI
    const handleReplaceWithManualImei = async () => {
        if (!manualImei.trim()) {
            toast.error('Veuillez saisir un IMEI');
            return;
        }
        await handleReplaceItem(manualImei.trim());
    };

    // Remove item without replacement (full refund to wallet)
    const handleRemoveItem = async () => {
        if (!selectedReason) {
            toast.error('Veuillez sélectionner une raison');
            return;
        }
        
        setReplacingItem(true);
        try {
            const response = await axios.post(
                `${API_URL}/admin/orders/${replaceModal.order.id}/remove-item`,
                {
                    imei: replaceModal.item.imei,
                    reason: selectedReason,
                    reason_details: reasonDetails || null
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(
                <div>
                    <strong>Article retiré</strong>
                    <div className="text-sm text-green-600">
                        Avoir de {response.data.avoir?.amount}€ crédité au wallet
                    </div>
                </div>
            );
            
            setReplaceModal({ open: false, order: null, item: null });
            fetchOrders();
            if (replaceModal.order?.id) {
                fetchVerificationStatus(replaceModal.order.id);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du retrait');
        } finally {
            setReplacingItem(false);
        }
    };

    // Download avoir PDF
    const handleDownloadAvoir = async (orderId, avoirId, avoirNumber) => {
        try {
            const response = await axios.get(
                `${API_URL}/admin/orders/${orderId}/avoir/${avoirId}/pdf`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );
            
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Avoir_${avoirNumber}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Avoir téléchargé');
        } catch (error) {
            toast.error('Erreur téléchargement avoir');
        }
    };

    // ========== PARTIAL SHIPMENT FUNCTIONS ==========
    
    // Toggle item selection for partial shipment
    const toggleItemForShipment = (orderId, imei) => {
        setSelectedForShipment(prev => {
            const current = prev[orderId] || [];
            if (current.includes(imei)) {
                return { ...prev, [orderId]: current.filter(i => i !== imei) };
            } else {
                return { ...prev, [orderId]: [...current, imei] };
            }
        });
    };

    // Select all verified items for shipment
    const selectAllVerifiedItems = (order) => {
        const verifiedImeis = order.verified_imeis || [];
        const alreadyShippedImeis = (order.shipments || []).flatMap(s => s.items || []);
        const availableForShipment = verifiedImeis.filter(imei => !alreadyShippedImeis.includes(imei));
        
        setSelectedForShipment(prev => ({
            ...prev,
            [order.id]: availableForShipment
        }));
    };

    // Select all non-shipped items (for removal)
    const selectAllNonShippedItems = (order) => {
        const alreadyShippedImeis = (order.shipments || []).flatMap(s => s.items || []);
        const allImeis = order.items?.map(item => item.imei) || [];
        const nonShippedImeis = allImeis.filter(imei => !alreadyShippedImeis.includes(imei));
        
        setSelectedForShipment(prev => ({
            ...prev,
            [order.id]: nonShippedImeis
        }));
    };

    // Clear selection for an order
    const clearShipmentSelection = (orderId) => {
        setSelectedForShipment(prev => ({
            ...prev,
            [orderId]: []
        }));
    };

    // Get items available for shipment (verified but not yet shipped)
    const getAvailableForShipment = (order) => {
        const verifiedImeis = order.verified_imeis || [];
        const alreadyShippedImeis = (order.shipments || []).flatMap(s => s.items || []);
        return verifiedImeis.filter(imei => !alreadyShippedImeis.includes(imei));
    };

    // Get all non-shipped items (for selection/removal)
    const getNonShippedItems = (order) => {
        const alreadyShippedImeis = (order.shipments || []).flatMap(s => s.items || []);
        return order.items?.filter(item => !alreadyShippedImeis.includes(item.imei)) || [];
    };

    // Check if an item is already shipped
    const isItemShipped = (order, imei) => {
        const alreadyShippedImeis = (order.shipments || []).flatMap(s => s.items || []);
        return alreadyShippedImeis.includes(imei);
    };

    // Open partial shipment modal
    const handleOpenPartialShipModal = (order) => {
        const selected = selectedForShipment[order.id] || [];
        if (selected.length === 0) {
            toast.error('Veuillez sélectionner au moins un article à expédier');
            return;
        }
        setPartialShipModal({ open: true, order });
        setPartialShipmentData({ carrier: '', trackingNumber: '', hasTracking: true });
    };

    // Create partial shipment
    const handleCreatePartialShipment = async () => {
        const order = partialShipModal.order;
        if (!order) return;
        
        const selected = selectedForShipment[order.id] || [];
        if (selected.length === 0) {
            toast.error('Aucun article sélectionné');
            return;
        }
        
        if (!partialShipmentData.carrier) {
            toast.error('Veuillez sélectionner un transporteur');
            return;
        }
        
        if (partialShipmentData.hasTracking && !partialShipmentData.trackingNumber) {
            toast.error('Veuillez saisir un numéro de suivi');
            return;
        }
        
        setCreatingPartialShipment(true);
        try {
            const response = await axios.post(
                `${API_URL}/admin/orders/${order.id}/partial-shipment`,
                {
                    imeis: selected,
                    carrier: partialShipmentData.carrier,
                    tracking_number: partialShipmentData.trackingNumber || null,
                    has_tracking: partialShipmentData.hasTracking
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(
                <div>
                    <strong>Envoi partiel créé !</strong>
                    <p className="text-sm">{response.data.message}</p>
                    {!response.data.is_fully_shipped && (
                        <p className="text-xs text-orange-600 mt-1">
                            Reste {response.data.remaining_items} article(s) à expédier
                        </p>
                    )}
                </div>
            );
            
            // Clear selection and close modal
            clearShipmentSelection(order.id);
            setPartialShipModal({ open: false, order: null });
            fetchOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'envoi');
        } finally {
            setCreatingPartialShipment(false);
        }
    };

    // Get shipment status badge
    const getShipmentStatusBadge = (order) => {
        const totalItems = order.items?.length || 0;
        const shipments = order.shipments || [];
        const shippedItems = shipments.reduce((sum, s) => sum + (s.items?.length || 0), 0);
        
        if (shippedItems === 0) {
            return null;
        } else if (shippedItems < totalItems) {
            return (
                <Badge className="bg-orange-100 text-orange-700 text-xs">
                    {shippedItems}/{totalItems} expédiés
                </Badge>
            );
        } else {
            return (
                <Badge className="bg-green-100 text-green-700 text-xs">
                    Tout expédié
                </Badge>
            );
        }
    };

    // Open bulk removal modal
    const handleOpenBulkRemovalModal = (order) => {
        const selected = selectedForShipment[order.id] || [];
        const alreadyShippedImeis = (order.shipments || []).flatMap(s => s.items || []);
        const removableItems = selected.filter(imei => !alreadyShippedImeis.includes(imei));
        
        if (removableItems.length === 0) {
            toast.error('Aucun article sélectionné pouvant être retiré');
            return;
        }
        
        setBulkRemovalModal({ open: true, order });
        setBulkRemovalReason('');
        setBulkRemovalDetails('');
        // Auto-check skip wallet for bank transfer orders that may not be fully paid
        setSkipWalletCredit(order.payment_method === 'bank_transfer');
    };

    // Execute bulk removal
    const handleBulkRemoval = async () => {
        const order = bulkRemovalModal.order;
        if (!order) return;
        
        const selected = selectedForShipment[order.id] || [];
        const alreadyShippedImeis = (order.shipments || []).flatMap(s => s.items || []);
        const removableItems = selected.filter(imei => !alreadyShippedImeis.includes(imei));
        
        if (removableItems.length === 0) {
            toast.error('Aucun article à retirer');
            return;
        }
        
        if (!bulkRemovalReason) {
            toast.error('Veuillez sélectionner une raison');
            return;
        }
        
        setRemovingBulk(true);
        try {
            const response = await axios.post(
                `${API_URL}/admin/orders/${order.id}/remove-items-bulk`,
                {
                    imeis: removableItems,
                    reason: bulkRemovalReason,
                    reason_details: bulkRemovalDetails || null,
                    skip_wallet_credit: skipWalletCredit
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const walletSkipped = response.data.wallet_credit?.skipped;
            toast.success(
                <div>
                    <strong>{response.data.removed_items_count} article(s) retiré(s)</strong>
                    <p className={`text-sm ${walletSkipped ? 'text-orange-600' : 'text-green-600'}`}>
                        {walletSkipped 
                            ? `Montant de ${response.data.avoir?.amount?.toFixed(2)}€ déduit (sans crédit wallet)`
                            : `Avoir de ${response.data.avoir?.amount?.toFixed(2)}€ crédité au wallet`
                        }
                    </p>
                </div>
            );
            
            clearShipmentSelection(order.id);
            setBulkRemovalModal({ open: false, order: null });
            setSkipWalletCredit(false);
            fetchOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du retrait');
        } finally {
            setRemovingBulk(false);
        }
    };

    // Get removable items from selection (not yet shipped)
    const getRemovableFromSelection = (order) => {
        const selected = selectedForShipment[order.id] || [];
        const alreadyShippedImeis = (order.shipments || []).flatMap(s => s.items || []);
        return selected.filter(imei => !alreadyShippedImeis.includes(imei));
    };

    // Get verified items from selection (can be shipped)
    const getVerifiedFromSelection = (order) => {
        const selected = selectedForShipment[order.id] || [];
        const alreadyShippedImeis = (order.shipments || []).flatMap(s => s.items || []);
        const verifiedImeis = verificationStatus[order.id]?.items
            ?.filter(i => i.verified)
            ?.map(i => i.imei) || [];
        return selected.filter(imei => !alreadyShippedImeis.includes(imei) && verifiedImeis.includes(imei));
    };

    const ordersToPreapre = orders.filter(o => ['confirmed', 'preparing', 'partially_shipped'].includes(o.status));
    const shippedOrders = orders.filter(o => o.status === 'shipped');

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Préparation des commandes</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        {ordersToPreapre.length} commande(s) à préparer • {shippedOrders.length} expédiée(s)
                    </p>
                </div>
                <Button variant="outline" onClick={fetchOrders} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Actualiser
                </Button>
            </div>

            {/* Orders to prepare */}
            <div className="space-y-4">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    Commandes à préparer ({ordersToPreapre.length})
                </h2>

                {ordersToPreapre.length === 0 ? (
                    <Card className="border-dashed">
                        <CardContent className="py-12 text-center">
                            <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-4" />
                            <p className="text-slate-500">Aucune commande en attente de préparation</p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="text-left p-3 font-semibold text-slate-700">Commande</th>
                                    <th className="text-left p-3 font-semibold text-slate-700">Client</th>
                                    <th className="text-left p-3 font-semibold text-slate-700">Articles</th>
                                    <th className="text-left p-3 font-semibold text-slate-700">Transport</th>
                                    <th className="text-right p-3 font-semibold text-slate-700">Total</th>
                                    <th className="text-right p-3 font-semibold text-slate-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {ordersToPreapre.map((order) => (
                                    <React.Fragment key={order.id}>
                                        <tr 
                                            className="hover:bg-slate-50 cursor-pointer"
                                            onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                                        >
                                            <td className="p-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono font-bold">#{order.id.slice(0, 8).toUpperCase()}</span>
                                                    {getStatusBadge(order.status)}
                                                </div>
                                                <p className="text-xs text-slate-500 mt-1">
                                                    {new Date(order.created_at).toLocaleDateString('fr-FR')}
                                                </p>
                                            </td>
                                            <td className="p-3">
                                                <p className="font-medium">{order.delivery_address?.name || order.company_name}</p>
                                                <p className="text-xs text-slate-500">{order.user_email}</p>
                                            </td>
                                            <td className="p-3">
                                                <div className="flex items-center gap-2">
                                                    <Package className="w-4 h-4 text-slate-400" />
                                                    <span>{order.items?.length || 0} article(s)</span>
                                                </div>
                                                {verificationStatus[order.id] && (
                                                    <p className={`text-xs mt-1 ${
                                                        verificationStatus[order.id].all_verified 
                                                            ? 'text-green-600' 
                                                            : 'text-amber-600'
                                                    }`}>
                                                        {verificationStatus[order.id].verified_count}/{verificationStatus[order.id].total_count} vérifié(s)
                                                    </p>
                                                )}
                                            </td>
                                            <td className="p-3">
                                                <p className="text-sm">{order.shipping_method || 'Non défini'}</p>
                                                {order.payment_method === 'stripe' && (
                                                    <Badge className="bg-indigo-100 text-indigo-800 text-xs mt-1">CB</Badge>
                                                )}
                                                {order.payment_method === 'bank_transfer' && (
                                                    <Badge className="bg-amber-100 text-amber-800 text-xs mt-1">Virement</Badge>
                                                )}
                                            </td>
                                            <td className="p-3 text-right">
                                                <p className="font-bold text-slate-900">
                                                    {(order.original_total_ttc || order.total_ttc + (order.credit_applied || 0)).toFixed(2)} €
                                                </p>
                                                {order.credit_applied > 0 && (
                                                    <p className="text-xs text-green-600">
                                                        -{order.credit_applied.toFixed(2)}€ crédit
                                                    </p>
                                                )}
                                            </td>
                                            <td className="p-3 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDownloadPackingSlip(order);
                                                        }}
                                                        title="Bon de commande"
                                                        disabled={processingAction === `packing-${order.id}`}
                                                    >
                                                        {processingAction === `packing-${order.id}` ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <FileText className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setExpandedOrder(expandedOrder === order.id ? null : order.id);
                                                        }}
                                                        title={expandedOrder === order.id ? "Masquer" : "Préparer"}
                                                        className="text-blue-600"
                                                    >
                                                        {expandedOrder === order.id ? (
                                                            <ChevronUp className="w-4 h-4" />
                                                        ) : (
                                                            <ChevronDown className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>

                                        {/* Expanded Details Row */}
                                        {expandedOrder === order.id && (
                                            <tr>
                                                <td colSpan={6} className="p-0">
                                                    <div className="bg-slate-50 border-t border-slate-200 p-4">
                                                        {/* IMEI Scanner Section */}
                                                        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                                                            <div className="flex items-center justify-between mb-3">
                                                                <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                                                                    <ScanBarcode className="w-5 h-5" />
                                                                    Scanner les IMEI
                                                                </h4>
                                                                {verificationStatus[order.id] && (
                                                                    <div className="flex items-center gap-3">
                                                                        <span className={`text-sm font-bold ${
                                                                            verificationStatus[order.id].all_verified 
                                                                                ? 'text-green-600' 
                                                                                : 'text-blue-600'
                                                                        }`}>
                                                                            {verificationStatus[order.id].verified_count}/{verificationStatus[order.id].total_count} vérifié(s)
                                                                        </span>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => handleResetVerification(order.id)}
                                                                            className="text-slate-500 hover:text-red-600"
                                                                        >
                                                                            <RotateCcw className="w-4 h-4" />
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex gap-2">
                                                                <Input
                                                                    ref={scanInputRef}
                                                                    type="text"
                                                                    placeholder="Scannez ou saisissez l'IMEI..."
                                                                    value={scanInput}
                                                                    onChange={(e) => setScanInput(e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            handleScanIMEI(order.id, scanInput);
                                                                        }
                                                                    }}
                                                                    className="flex-1 font-mono bg-white"
                                                                    data-testid="imei-scan-input"
                                                                />
                                                                <Button
                                                                    onClick={() => handleScanIMEI(order.id, scanInput)}
                                                                    disabled={!scanInput.trim()}
                                                                    className="bg-blue-600 hover:bg-blue-700"
                                                                >
                                                                    Vérifier
                                                                </Button>
                                                            </div>
                                                        </div>

                                                        <div className="grid md:grid-cols-3 gap-6">
                                                            {/* Products with verification status */}
                                                            <div>
                                                                <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                                                    <Package className="w-4 h-4" />
                                                                    Articles à préparer
                                                                    {getShipmentStatusBadge(order)}
                                                                </h4>
                                                                
                                                                {/* Selection controls for partial shipment */}
                                                                {getNonShippedItems(order).length > 0 && (
                                                                    <div className="flex flex-wrap gap-2 mb-3">
                                                                        {getAvailableForShipment(order).length > 0 && (
                                                                            <Button
                                                                                variant="outline"
                                                                                size="sm"
                                                                                onClick={() => selectAllVerifiedItems(order)}
                                                                                className="text-xs border-green-300 text-green-700 hover:bg-green-50"
                                                                            >
                                                                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                                                                Sélectionner vérifiés ({getAvailableForShipment(order).length})
                                                                            </Button>
                                                                        )}
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            onClick={() => selectAllNonShippedItems(order)}
                                                                            className="text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                                                                        >
                                                                            <Package className="w-3 h-3 mr-1" />
                                                                            Sélectionner non expédiés ({getNonShippedItems(order).length})
                                                                        </Button>
                                                                        {(selectedForShipment[order.id]?.length || 0) > 0 && (
                                                                            <Button
                                                                                variant="outline"
                                                                                size="sm"
                                                                                onClick={() => clearShipmentSelection(order.id)}
                                                                                className="text-xs text-slate-500"
                                                                            >
                                                                                Désélectionner
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                
                                                                <div className="space-y-2">
                                                                    {order.items?.map((item, idx) => {
                                                                        const isVerified = verificationStatus[order.id]?.items?.find(
                                                                            i => i.imei === item.imei
                                                                        )?.verified;
                                                                        const isShipped = isItemShipped(order, item.imei);
                                                                        const isSelected = selectedForShipment[order.id]?.includes(item.imei);
                                                                        // Can select if: verified AND not shipped (for shipping)
                                                                        // OR not shipped (for removal - even if not verified)
                                                                        const canSelectForShipping = isVerified && !isShipped;
                                                                        const canSelectForRemoval = !isShipped;
                                                                        
                                                                        return (
                                                                            <div 
                                                                                key={idx} 
                                                                                className={`rounded-lg p-3 border-2 transition-all ${
                                                                                    isShipped
                                                                                        ? 'bg-blue-50 border-blue-300'
                                                                                        : isVerified 
                                                                                            ? isSelected
                                                                                                ? 'bg-green-100 border-green-400 ring-2 ring-green-300'
                                                                                                : 'bg-green-50 border-green-300' 
                                                                                            : isSelected
                                                                                                ? 'bg-orange-100 border-orange-400 ring-2 ring-orange-300'
                                                                                                : 'bg-white border-slate-200'
                                                                                }`}
                                                                            >
                                                                                <div className="flex items-start gap-3">
                                                                                    {/* Checkbox for selection - available for all non-shipped items */}
                                                                                    {canSelectForRemoval && (
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={isSelected}
                                                                                            onChange={() => toggleItemForShipment(order.id, item.imei)}
                                                                                            className={`mt-1 h-4 w-4 rounded border-gray-300 focus:ring-blue-500 ${
                                                                                                isVerified ? 'text-blue-600' : 'text-orange-600'
                                                                                            }`}
                                                                                            title={isVerified ? "Sélectionner pour expédition" : "Sélectionner pour retrait (non vérifié)"}
                                                                                        />
                                                                                    )}
                                                                                    {isShipped && (
                                                                                        <div className="mt-1">
                                                                                            <Truck className="w-4 h-4 text-blue-500" />
                                                                                        </div>
                                                                                    )}
                                                                                    
                                                                                    <div className="flex-1">
                                                                                        <p className="font-medium text-slate-900">
                                                                                            {item.brand} {item.model}
                                                                                        </p>
                                                                                        <p className="text-sm text-slate-500">
                                                                                            {item.storage_capacity}GB • {item.color} • Grade {item.grade}
                                                                                        </p>
                                                                                        <p className="text-xs font-mono text-slate-400 mt-1">
                                                                                            IMEI: {item.imei}
                                                                                        </p>
                                                                                        {/* Replacement info badge */}
                                                                                        {item.replaced_from_imei && (
                                                                                            <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                                                                                                <Replace className="w-3 h-3" />
                                                                                                Remplacé
                                                                                            </p>
                                                                                        )}
                                                                                        {/* Shipped badge */}
                                                                                        {isShipped && (
                                                                                            <Badge className="mt-1 bg-blue-100 text-blue-700 text-xs">
                                                                                                Expédié
                                                                                            </Badge>
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="ml-2 flex flex-col items-center gap-1">
                                                                                        {isShipped ? (
                                                                                            <CheckCircle className="w-6 h-6 text-blue-500" />
                                                                                        ) : isVerified ? (
                                                                                            <CheckCircle2 className="w-6 h-6 text-green-500" />
                                                                                        ) : (
                                                                                            <>
                                                                                                <Clock className="w-6 h-6 text-slate-300" />
                                                                                                <Button
                                                                                                    variant="ghost"
                                                                                                    size="sm"
                                                                                                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 p-1 h-auto"
                                                                                                    onClick={() => handleOpenReplaceModal(order, item)}
                                                                                                    title="Remplacer ou retirer cet article"
                                                                                                >
                                                                                                    <Replace className="w-4 h-4" />
                                                                                                </Button>
                                                                                            </>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                                
                                                                {/* Partial shipment button */}
                                                                {(selectedForShipment[order.id]?.length || 0) > 0 && (
                                                                    <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                                                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                                                            <div className="text-sm">
                                                                                <span className="font-medium text-slate-800">
                                                                                    {selectedForShipment[order.id].length} article(s) sélectionné(s)
                                                                                </span>
                                                                                {getVerifiedFromSelection(order).length < selectedForShipment[order.id].length && (
                                                                                    <span className="text-orange-600 ml-2">
                                                                                        ({selectedForShipment[order.id].length - getVerifiedFromSelection(order).length} non vérifié(s))
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <div className="flex gap-2">
                                                                                {/* Bouton Retirer (en rouge) - toujours disponible */}
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="destructive"
                                                                                    onClick={() => handleOpenBulkRemovalModal(order)}
                                                                                    title="Retirer les articles sélectionnés"
                                                                                >
                                                                                    <Trash2 className="w-4 h-4 mr-1" />
                                                                                    Retirer ({getRemovableFromSelection(order).length})
                                                                                </Button>
                                                                                {/* Bouton Expédier (en vert) - seulement si articles vérifiés */}
                                                                                <Button
                                                                                    size="sm"
                                                                                    className="bg-green-600 hover:bg-green-700"
                                                                                    onClick={() => handleOpenPartialShipModal(order)}
                                                                                    disabled={getVerifiedFromSelection(order).length === 0}
                                                                                    title={getVerifiedFromSelection(order).length === 0 
                                                                                        ? "Aucun article vérifié sélectionné" 
                                                                                        : "Expédier les articles vérifiés sélectionnés"}
                                                                                >
                                                                                    <Truck className="w-4 h-4 mr-1" />
                                                                                    Expédier ({getVerifiedFromSelection(order).length})
                                                                                </Button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                
                                                                {/* Existing shipments */}
                                                                {(order.shipments?.length || 0) > 0 && (
                                                                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                                                        <h5 className="text-sm font-semibold text-blue-800 mb-2">
                                                                            Envois effectués ({order.shipments.length})
                                                                        </h5>
                                                                        <div className="space-y-2">
                                                                            {order.shipments.map((shipment, sIdx) => (
                                                                                <div key={sIdx} className="flex items-center justify-between bg-white rounded p-2 text-sm">
                                                                                    <div>
                                                                                        <span className="font-medium">{shipment.shipment_ref}</span>
                                                                                        <span className="text-slate-500 ml-2">
                                                                                            ({shipment.items_count} pcs) - {shipment.carrier_name}
                                                                                        </span>
                                                                                    </div>
                                                                                    {shipment.tracking_number && (
                                                                                        <a
                                                                                            href={shipment.tracking_url || '#'}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className="text-blue-600 hover:underline flex items-center gap-1"
                                                                                        >
                                                                                            {shipment.tracking_number}
                                                                                            <ExternalLink className="w-3 h-3" />
                                                                                        </a>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Delivery Address */}
                                                            <div>
                                                                <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                                                    <MapPin className="w-4 h-4" />
                                                                    Adresse de livraison
                                                                </h4>
                                                                <div className="bg-white rounded-lg p-3 border border-slate-200">
                                                                    <p className="font-medium">{order.delivery_address?.name}</p>
                                                                    <p className="text-sm text-slate-600">{order.delivery_address?.street}</p>
                                                                    {order.delivery_address?.street2 && (
                                                                        <p className="text-sm text-slate-600">{order.delivery_address?.street2}</p>
                                                                    )}
                                                                    <p className="text-sm text-slate-600">
                                                                        {order.delivery_address?.postal_code} {order.delivery_address?.city}
                                                                    </p>
                                                                    {order.delivery_address?.phone && (
                                                                        <p className="text-sm text-slate-500 mt-2 flex items-center gap-1">
                                                                            <Phone className="w-3 h-3" />
                                                                            {order.delivery_address?.phone}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Actions */}
                                                            <div>
                                                                <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                                                    <Truck className="w-4 h-4" />
                                                                    Actions
                                                                </h4>
                                                                <div className="space-y-2">
                                                                    {/* Expédier button - opens shipping modal */}
                                                                    {verificationStatus[order.id]?.all_verified ? (
                                                                        <Button
                                                                            className="w-full justify-start bg-green-600 hover:bg-green-700"
                                                                            onClick={() => {
                                                                                setShipModal({ open: true, order: order });
                                                                                setManualShipment({ carrier: '', trackingNumber: '', hasTracking: true });
                                                                            }}
                                                                            disabled={processingAction === `ship-${order.id}`}
                                                                        >
                                                                            <Send className="w-4 h-4 mr-2" />
                                                                            Expédier la commande
                                                                        </Button>
                                                                    ) : (
                                                                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                                                            <p className="text-xs text-amber-600 flex items-center gap-2">
                                                                                <AlertTriangle className="w-4 h-4" />
                                                                                Scannez tous les IMEI avant de pouvoir expédier
                                                                            </p>
                                                                            <p className="text-xs text-amber-500 mt-1">
                                                                                {verificationStatus[order.id]?.verified_count || 0}/{verificationStatus[order.id]?.total_count || order.items?.length} vérifié(s)
                                                                            </p>
                                                                        </div>
                                                                    )}

                                                                    {/* Cancel button */}
                                                                    <Button
                                                                        variant="outline"
                                                                        className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                                                        onClick={() => {
                                                                            setCancelModal({ open: true, order: order });
                                                                            setCancellationReason('');
                                                                        }}
                                                                    >
                                                                        <Ban className="w-4 h-4 mr-2" />
                                                                        Annuler
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Shipped orders */}
            {shippedOrders.length > 0 && (
                <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                        <Truck className="w-5 h-5 text-green-600" />
                        Commandes expédiées ({shippedOrders.length})
                    </h2>

                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="text-left p-3 font-semibold text-slate-700">Commande</th>
                                    <th className="text-left p-3 font-semibold text-slate-700">Client</th>
                                    <th className="text-left p-3 font-semibold text-slate-700">Expédition</th>
                                    <th className="text-left p-3 font-semibold text-slate-700">Tracking</th>
                                    <th className="text-left p-3 font-semibold text-slate-700">Facture</th>
                                    <th className="text-right p-3 font-semibold text-slate-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {shippedOrders.map((order) => (
                                    <tr key={order.id} className="hover:bg-slate-50">
                                        <td className="p-3">
                                            <span className="font-mono font-bold">#{order.id.slice(0, 8)}</span>
                                            <p className="text-xs text-slate-500">
                                                {new Date(order.created_at).toLocaleDateString('fr-FR')}
                                            </p>
                                        </td>
                                        <td className="p-3">
                                            <p className="font-medium">{order.delivery_address?.name}</p>
                                            <p className="text-xs text-slate-500">{order.user_email}</p>
                                        </td>
                                        <td className="p-3">
                                            <p className="text-sm">{order.shipping_method}</p>
                                            <p className="text-xs text-slate-500">
                                                {order.shipped_at ? new Date(order.shipped_at).toLocaleDateString('fr-FR') : '-'}
                                            </p>
                                        </td>
                                        <td className="p-3">
                                            {order.tracking_number ? (
                                                <a 
                                                    href={order.tracking_url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:underline text-sm"
                                                >
                                                    {order.tracking_number}
                                                </a>
                                            ) : (
                                                <span className="text-slate-400">-</span>
                                            )}
                                        </td>
                                        <td className="p-3">
                                            {order.pennylane_invoice_number ? (
                                                <Badge className="bg-green-100 text-green-800">
                                                    {order.pennylane_invoice_number}
                                                </Badge>
                                            ) : order.invoice_number ? (
                                                <Badge className="bg-amber-100 text-amber-800" title="Facture interne (pas Pennylane)">
                                                    {order.invoice_number}
                                                </Badge>
                                            ) : (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleGenerateInvoice(order)}
                                                    disabled={processingAction === `gen-invoice-${order.id}`}
                                                    className="text-xs"
                                                >
                                                    {processingAction === `gen-invoice-${order.id}` ? (
                                                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                                    ) : (
                                                        <FileText className="w-3 h-3 mr-1" />
                                                    )}
                                                    Générer
                                                </Button>
                                            )}
                                        </td>
                                        <td className="p-3 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                {/* Bon de commande */}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDownloadPackingSlip(order)}
                                                    title="Bon de commande"
                                                >
                                                    <FileText className="w-4 h-4" />
                                                </Button>
                                                {/* Bon de transport */}
                                                {order.shipment?.reference && (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDownloadLabel(order)}
                                                            disabled={processingAction === `download-${order.id}`}
                                                            title="Télécharger le bon de transport"
                                                            className="text-indigo-600"
                                                            data-testid="download-label-btn"
                                                        >
                                                            {processingAction === `download-${order.id}` ? (
                                                                <RefreshCw className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <Truck className="w-4 h-4" />
                                                            )}
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleOpenBoxtal(order)}
                                                            title={`Ouvrir sur Boxtal (Réf: ${order.shipment?.reference})`}
                                                            className="text-orange-500"
                                                            data-testid="open-boxtal-btn"
                                                        >
                                                            <ExternalLink className="w-4 h-4" />
                                                        </Button>
                                                    </>
                                                )}
                                                {/* Facture */}
                                                {(order.invoice_number || order.pennylane_invoice_id) && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDownloadInvoice(order)}
                                                        title="Facture"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Cancellation Modal */}
            <Dialog 
                open={cancelModal.open} 
                onOpenChange={(open) => {
                    if (!open) {
                        setCancelModal({ open: false, order: null });
                        setCancellationReason('');
                    }
                }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <Ban className="w-5 h-5" />
                            Annuler la commande
                        </DialogTitle>
                        <DialogDescription>
                            {cancelModal.order && (
                                <span>
                                    Commande <strong>#{cancelModal.order.id?.slice(0, 8).toUpperCase()}</strong> - {cancelModal.order.user_email}
                                </span>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-sm text-amber-800">
                                <strong>Attention:</strong> Cette action remettra les produits en stock et notifiera l&apos;ERP de l&apos;annulation.
                            </p>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="cancel-reason" className="text-sm font-medium">
                                Motif d&apos;annulation <span className="text-red-500">*</span>
                            </Label>
                            <Textarea
                                id="cancel-reason"
                                value={cancellationReason}
                                onChange={(e) => setCancellationReason(e.target.value)}
                                placeholder="Indiquez la raison de l&apos;annulation..."
                                className="min-h-[100px]"
                            />
                        </div>
                    </div>
                    
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setCancelModal({ open: false, order: null });
                                setCancellationReason('');
                            }}
                        >
                            Retour
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={!cancellationReason.trim() || processingAction === `cancel-${cancelModal.order?.id}`}
                            onClick={handleCancelOrder}
                        >
                            {processingAction === `cancel-${cancelModal.order?.id}` ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Ban className="w-4 h-4 mr-2" />
                            )}
                            Confirmer l&apos;annulation
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Shipping Modal */}
            <Dialog 
                open={shipModal.open} 
                onOpenChange={(open) => {
                    if (!open) {
                        setShipModal({ open: false, order: null });
                        setManualShipment({ carrier: '', trackingNumber: '', hasTracking: true });
                    }
                }}
            >
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Truck className="w-5 h-5 text-indigo-600" />
                            Préparer l&apos;expédition
                        </DialogTitle>
                        <DialogDescription>
                            {shipModal.order && (
                                <span>
                                    Commande <strong>#{shipModal.order.id?.slice(0, 8).toUpperCase()}</strong> - {shipModal.order.user_email}
                                </span>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {shipModal.order && (
                        <div className="space-y-6 py-4">
                            {/* Delivery address */}
                            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                                <h4 className="font-medium text-slate-700 flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    Adresse de livraison
                                </h4>
                                {shipModal.order.delivery_address ? (
                                    <div className="text-sm text-slate-600">
                                        <p className="font-medium">{shipModal.order.delivery_address.name || shipModal.order.company_name}</p>
                                        <p>{shipModal.order.delivery_address.street}</p>
                                        {shipModal.order.delivery_address.street2 && <p>{shipModal.order.delivery_address.street2}</p>}
                                        <p>{shipModal.order.delivery_address.postal_code} {shipModal.order.delivery_address.city}</p>
                                        {shipModal.order.delivery_address.phone && <p>Tél: {shipModal.order.delivery_address.phone}</p>}
                                    </div>
                                ) : (
                                    <p className="text-amber-600">Adresse non renseignée</p>
                                )}
                                <p className="text-sm text-indigo-600 mt-2">
                                    <strong>Transport:</strong> {shipModal.order.shipping_method || 'Non spécifié'}
                                </p>
                            </div>

                            {/* Two options: Boxtal or Manual */}
                            <div className="grid md:grid-cols-2 gap-4">
                                {/* Option 1: Boxtal */}
                                <div className="border-2 border-indigo-200 rounded-xl p-4 space-y-3 bg-indigo-50/30">
                                    <h4 className="font-semibold text-indigo-800 flex items-center gap-2">
                                        <Package className="w-5 h-5" />
                                        Option 1: Via Boxtal
                                    </h4>
                                    
                                    {shipModal.order.shipment?.reference ? (
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2 text-green-600 text-sm">
                                                <CheckCircle className="w-4 h-4" />
                                                <span>Bon généré</span>
                                            </div>
                                            <p className="text-xs text-slate-600">
                                                Réf: {shipModal.order.shipment.reference}
                                            </p>
                                            {shipModal.order.tracking_number && (
                                                <p className="text-xs text-slate-600">
                                                    Tracking: {shipModal.order.tracking_number}
                                                </p>
                                            )}
                                            <Button
                                                size="sm"
                                                className="w-full bg-indigo-600 hover:bg-indigo-700"
                                                onClick={() => handleMarkShipped(shipModal.order, true)}
                                                disabled={processingAction === `ship-${shipModal.order.id}`}
                                            >
                                                {processingAction === `ship-${shipModal.order.id}` ? (
                                                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                                ) : (
                                                    <Send className="w-4 h-4 mr-1" />
                                                )}
                                                Confirmer expédition Boxtal
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <p className="text-sm text-slate-600">
                                                Aucun bon Boxtal généré.
                                            </p>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="w-full"
                                                onClick={() => handleGenerateLabel(shipModal.order)}
                                                disabled={processingAction === `label-${shipModal.order.id}`}
                                            >
                                                {processingAction === `label-${shipModal.order.id}` ? (
                                                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                                ) : (
                                                    <Package className="w-4 h-4 mr-1" />
                                                )}
                                                Générer bon Boxtal
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {/* Option 2: Manual */}
                                <div className="border-2 border-amber-200 rounded-xl p-4 space-y-3 bg-amber-50/30">
                                    <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                                        <Upload className="w-5 h-5" />
                                        Option 2: Expédition Manuelle
                                    </h4>
                                    <p className="text-xs text-slate-600">
                                        Expédiez avec votre propre transporteur
                                    </p>
                                    
                                    {/* Carrier selection */}
                                    <div>
                                        <Label className="text-xs">Transporteur *</Label>
                                        <Select 
                                            value={manualShipment.carrier} 
                                            onValueChange={(val) => setManualShipment(prev => ({ ...prev, carrier: val }))}
                                        >
                                            <SelectTrigger className="mt-1">
                                                <SelectValue placeholder="Choisir..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {MANUAL_CARRIERS.map(c => (
                                                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Has tracking checkbox */}
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="hasTrackingPrep"
                                            checked={manualShipment.hasTracking}
                                            onChange={(e) => setManualShipment(prev => ({ ...prev, hasTracking: e.target.checked }))}
                                            className="rounded border-slate-300"
                                        />
                                        <Label htmlFor="hasTrackingPrep" className="text-sm cursor-pointer">
                                            Avec numéro de suivi
                                        </Label>
                                    </div>

                                    {manualShipment.hasTracking && (
                                        <div>
                                            <Label className="text-xs">N° de suivi</Label>
                                            <Input
                                                value={manualShipment.trackingNumber}
                                                onChange={(e) => setManualShipment(prev => ({ ...prev, trackingNumber: e.target.value }))}
                                                placeholder="Ex: 1Z999AA10123456784"
                                                className="mt-1 text-sm"
                                            />
                                        </div>
                                    )}

                                    {!manualShipment.hasTracking && (
                                        <div className="p-2 bg-amber-100 rounded text-xs text-amber-800">
                                            ⚠️ Le client ne pourra pas suivre son colis
                                        </div>
                                    )}

                                    {/* Upload label (optional) */}
                                    <div>
                                        <Label className="text-xs">Bon de transport (PDF, optionnel)</Label>
                                        <div className="mt-1 flex items-center gap-2">
                                            <Input
                                                type="file"
                                                accept=".pdf"
                                                onChange={(e) => {
                                                    if (e.target.files?.[0]) {
                                                        handleUploadLabel(shipModal.order.id, e.target.files[0]);
                                                    }
                                                }}
                                                className="text-xs"
                                                disabled={uploadingLabel}
                                            />
                                            {uploadingLabel && <Loader2 className="w-4 h-4 animate-spin" />}
                                        </div>
                                        {shipModal.order.manual_shipping_label && (
                                            <p className="text-xs text-green-600 mt-1">✓ Bon uploadé</p>
                                        )}
                                    </div>

                                    <Button
                                        size="sm"
                                        className="w-full bg-amber-600 hover:bg-amber-700"
                                        disabled={!manualShipment.carrier || processingAction === `ship-${shipModal.order.id}`}
                                        onClick={() => handleManualShipment(shipModal.order)}
                                    >
                                        {processingAction === `ship-${shipModal.order.id}` ? (
                                            <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                        ) : (
                                            <Send className="w-4 h-4 mr-1" />
                                        )}
                                        Expédier {!manualShipment.hasTracking ? '(sans suivi)' : ''}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShipModal({ open: false, order: null });
                                setManualShipment({ carrier: '', trackingNumber: '', hasTracking: true });
                            }}
                        >
                            Fermer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Replacement Modal */}
            <Dialog open={replaceModal.open} onOpenChange={(open) => !open && setReplaceModal({ open: false, order: null, item: null })}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Replace className="w-5 h-5 text-orange-600" />
                            Remplacer ou retirer un article
                        </DialogTitle>
                        <DialogDescription>
                            Commande #{replaceModal.order?.id?.slice(0, 8)}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {replaceModal.item && (
                        <div className="space-y-6">
                            {/* Original item */}
                            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                                <h4 className="font-semibold text-slate-700 mb-2">Article concerné</h4>
                                <div className="flex justify-between items-center">
                                    <div>
                                        <p className="font-medium">{replaceModal.item.brand} {replaceModal.item.model}</p>
                                        <p className="text-sm text-slate-500">
                                            {replaceModal.item.storage_capacity}GB • {replaceModal.item.color} • Grade {replaceModal.item.grade}
                                        </p>
                                        <p className="text-xs font-mono text-slate-400">IMEI: {replaceModal.item.imei}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-lg font-bold text-slate-900">{replaceModal.item.price?.toFixed(2)} €</p>
                                    </div>
                                </div>
                            </div>

                            {/* Reason selection */}
                            <div>
                                <Label className="text-sm font-medium">Raison du remplacement/retrait *</Label>
                                <Select value={selectedReason} onValueChange={setSelectedReason}>
                                    <SelectTrigger className="mt-1">
                                        <SelectValue placeholder="Sélectionner une raison..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {REMOVAL_REASONS.map(r => (
                                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {selectedReason === 'other' && (
                                    <Textarea
                                        className="mt-2"
                                        placeholder="Précisez la raison..."
                                        value={reasonDetails}
                                        onChange={(e) => setReasonDetails(e.target.value)}
                                    />
                                )}
                            </div>

                            {/* Replacement suggestions */}
                            <div>
                                <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                    <Search className="w-4 h-4" />
                                    Produits de remplacement disponibles
                                    <span className="text-xs font-normal text-slate-400">
                                        (même modèle, prix ≤ {replaceModal.item.price?.toFixed(2)}€)
                                    </span>
                                </h4>

                                {loadingSuggestions ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                                    </div>
                                ) : replacementSuggestions.length > 0 ? (
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                        {replacementSuggestions.map((suggestion) => (
                                            <div 
                                                key={suggestion.id}
                                                className={`p-3 rounded-lg border-2 transition-all ${
                                                    suggestion.is_exact_match 
                                                        ? 'border-green-300 bg-green-50' 
                                                        : 'border-slate-200 bg-white hover:border-blue-300'
                                                }`}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-medium">{suggestion.brand} {suggestion.model}</p>
                                                            {suggestion.is_exact_match && (
                                                                <Badge className="bg-green-100 text-green-700 text-xs">
                                                                    Identique
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-slate-500">
                                                            {suggestion.storage_capacity}GB • {suggestion.color} • Grade {suggestion.grade}
                                                        </p>
                                                        <p className="text-xs font-mono text-slate-400">IMEI: {suggestion.imei}</p>
                                                    </div>
                                                    <div className="text-right ml-4">
                                                        <p className="font-bold text-slate-900">{suggestion.price?.toFixed(2)} €</p>
                                                        {suggestion.price_difference > 0 && (
                                                            <p className="text-xs text-green-600 flex items-center gap-1">
                                                                <Wallet className="w-3 h-3" />
                                                                Avoir: +{suggestion.price_difference?.toFixed(2)}€
                                                            </p>
                                                        )}
                                                        <Button
                                                            size="sm"
                                                            className="mt-2 bg-blue-600 hover:bg-blue-700"
                                                            disabled={!selectedReason || replacingItem}
                                                            onClick={() => handleReplaceItem(suggestion.imei)}
                                                        >
                                                            {replacingItem ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <>
                                                                    <Replace className="w-3 h-3 mr-1" />
                                                                    Remplacer
                                                                </>
                                                            )}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg">
                                        <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                                        <p>Aucun remplacement trouvé automatiquement</p>
                                        <p className="text-xs">Saisissez un IMEI manuellement ou retirez l&apos;article</p>
                                    </div>
                                )}
                            </div>

                            {/* Manual IMEI input */}
                            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                                    <ScanBarcode className="w-4 h-4" />
                                    Remplacement manuel par IMEI
                                </h4>
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Saisir l'IMEI du produit de remplacement..."
                                        value={manualImei}
                                        onChange={(e) => setManualImei(e.target.value)}
                                        className="flex-1 font-mono bg-white"
                                    />
                                    <Button
                                        onClick={handleReplaceWithManualImei}
                                        disabled={!manualImei.trim() || !selectedReason || replacingItem}
                                        className="bg-blue-600 hover:bg-blue-700"
                                    >
                                        {replacingItem ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <>
                                                <Replace className="w-4 h-4 mr-1" />
                                                Remplacer
                                            </>
                                        )}
                                    </Button>
                                </div>
                                <p className="text-xs text-blue-600 mt-2">
                                    Le produit doit être disponible et son prix ≤ {replaceModal.item.price?.toFixed(2)}€
                                </p>
                            </div>

                            {/* Remove without replacement */}
                            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                                <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                                    <Trash2 className="w-4 h-4" />
                                    Retirer sans remplacement
                                </h4>
                                <p className="text-sm text-red-700 mb-3">
                                    Un avoir de <strong>{replaceModal.item.price?.toFixed(2)} €</strong> sera généré et crédité sur le wallet du client.
                                </p>
                                <Button
                                    variant="destructive"
                                    onClick={handleRemoveItem}
                                    disabled={!selectedReason || replacingItem || (replaceModal.order?.items?.length || 0) <= 1}
                                    className="bg-red-600 hover:bg-red-700"
                                >
                                    {replacingItem ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                    ) : (
                                        <Trash2 className="w-4 h-4 mr-1" />
                                    )}
                                    Retirer et générer avoir
                                </Button>
                                {(replaceModal.order?.items?.length || 0) <= 1 && (
                                    <p className="text-xs text-red-500 mt-2">
                                        Impossible de retirer le dernier article. Annulez la commande si nécessaire.
                                    </p>
                                )}
                            </div>

                            {/* Order has avoirs */}
                            {replaceModal.order?.avoirs?.length > 0 && (
                                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                                    <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                                        <CreditCard className="w-4 h-4" />
                                        Avoirs générés sur cette commande
                                    </h4>
                                    <div className="space-y-2">
                                        {replaceModal.order.avoirs.map((avoir) => (
                                            <div key={avoir.id} className="flex justify-between items-center bg-white rounded p-2">
                                                <div>
                                                    <p className="text-sm font-medium">{avoir.number}</p>
                                                    <p className="text-xs text-slate-500">{avoir.type === 'replacement' ? 'Remplacement' : 'Retrait'}</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold text-green-600">{avoir.amount?.toFixed(2)} €</span>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => handleDownloadAvoir(replaceModal.order.id, avoir.id, avoir.number)}
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setReplaceModal({ open: false, order: null, item: null })}
                        >
                            Fermer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Partial Shipment Modal */}
            <Dialog open={partialShipModal.open} onOpenChange={(open) => !open && setPartialShipModal({ open: false, order: null })}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Truck className="w-5 h-5 text-green-600" />
                            Envoi partiel
                        </DialogTitle>
                        <DialogDescription>
                            Commande #{partialShipModal.order?.id?.slice(0, 8)} - {selectedForShipment[partialShipModal.order?.id]?.length || 0} article(s) sur {partialShipModal.order?.items?.length || 0}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        {/* Summary */}
                        <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                            <p className="text-sm text-green-800">
                                <strong>{selectedForShipment[partialShipModal.order?.id]?.length || 0}</strong> article(s) sélectionné(s) pour cet envoi
                            </p>
                            <p className="text-xs text-green-600 mt-1">
                                Un email avec le numéro de suivi sera envoyé au client
                            </p>
                        </div>

                        {/* Shipping method tabs */}
                        <Tabs defaultValue="boxtal" className="w-full">
                            <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="boxtal">Générer via Boxtal</TabsTrigger>
                                <TabsTrigger value="manual">Envoi manuel</TabsTrigger>
                            </TabsList>
                            
                            {/* Boxtal Tab */}
                            <TabsContent value="boxtal" className="space-y-4 mt-4">
                                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <p className="text-sm text-blue-800 mb-3">
                                        Utilisez le bouton &quot;Préparer l&apos;expédition&quot; dans la section Actions de la commande pour générer une étiquette Boxtal.
                                    </p>
                                    <p className="text-xs text-blue-600">
                                        Note : L&apos;étiquette Boxtal sera générée pour tous les articles restants non expédiés.
                                    </p>
                                </div>
                                <Button
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => {
                                        setPartialShipModal({ open: false, order: null });
                                        // Scroll to actions section
                                        toast.info('Utilisez le bouton "Préparer l\'expédition" dans la section Actions');
                                    }}
                                >
                                    Fermer et utiliser Boxtal
                                </Button>
                            </TabsContent>
                            
                            {/* Manual Tab */}
                            <TabsContent value="manual" className="space-y-4 mt-4">
                                {/* Carrier selection */}
                                <div>
                                    <Label className="text-sm font-medium">Transporteur *</Label>
                                    <Select 
                                        value={partialShipmentData.carrier} 
                                        onValueChange={(v) => setPartialShipmentData(prev => ({ ...prev, carrier: v }))}
                                    >
                                        <SelectTrigger className="mt-1">
                                            <SelectValue placeholder="Sélectionner un transporteur..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {MANUAL_CARRIERS.map(c => (
                                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Tracking toggle */}
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="hasTrackingPartial"
                                        checked={partialShipmentData.hasTracking}
                                        onChange={(e) => setPartialShipmentData(prev => ({ ...prev, hasTracking: e.target.checked }))}
                                        className="h-4 w-4 rounded border-gray-300"
                                    />
                                    <Label htmlFor="hasTrackingPartial" className="text-sm">Avec numéro de suivi</Label>
                                </div>

                                {/* Tracking number */}
                                {partialShipmentData.hasTracking && (
                                    <div>
                                        <Label className="text-sm font-medium">Numéro de suivi *</Label>
                                        <Input
                                            className="mt-1 font-mono"
                                            placeholder="Ex: 1Z999AA10123456784"
                                            value={partialShipmentData.trackingNumber}
                                            onChange={(e) => setPartialShipmentData(prev => ({ ...prev, trackingNumber: e.target.value }))}
                                        />
                                    </div>
                                )}
                                
                                <Button
                                    onClick={handleCreatePartialShipment}
                                    disabled={creatingPartialShipment || !partialShipmentData.carrier || (partialShipmentData.hasTracking && !partialShipmentData.trackingNumber)}
                                    className="w-full bg-green-600 hover:bg-green-700"
                                >
                                    {creatingPartialShipment ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                    ) : (
                                        <Truck className="w-4 h-4 mr-1" />
                                    )}
                                    Créer l&apos;envoi manuel
                                </Button>
                            </TabsContent>
                        </Tabs>
                    </div>
                    
                    <DialogFooter className="mt-4">
                        <Button
                            variant="outline"
                            onClick={() => setPartialShipModal({ open: false, order: null })}
                        >
                            Annuler
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Removal Modal */}
            <Dialog open={bulkRemovalModal.open} onOpenChange={(open) => !open && setBulkRemovalModal({ open: false, order: null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <Trash2 className="w-5 h-5" />
                            Retirer plusieurs articles
                        </DialogTitle>
                        <DialogDescription>
                            Commande #{bulkRemovalModal.order?.id?.slice(0, 8)}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {bulkRemovalModal.order && (
                        <div className="space-y-4">
                            {/* Summary */}
                            <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                                <p className="text-sm text-red-800">
                                    <strong>{getRemovableFromSelection(bulkRemovalModal.order).length}</strong> article(s) seront retirés de la commande
                                </p>
                                <p className="text-sm text-red-700 mt-1">
                                    Un avoir sera généré et crédité sur le wallet du client.
                                </p>
                            </div>

                            {/* Items to remove */}
                            <div className="max-h-40 overflow-y-auto bg-slate-50 rounded-lg p-2">
                                {bulkRemovalModal.order.items
                                    ?.filter(item => getRemovableFromSelection(bulkRemovalModal.order).includes(item.imei))
                                    .map((item, idx) => (
                                        <div key={idx} className="text-sm py-1 border-b border-slate-200 last:border-0">
                                            <span className="font-medium">{item.brand} {item.model}</span>
                                            <span className="text-slate-500 ml-2">{item.price?.toFixed(2)}€</span>
                                        </div>
                                    ))
                                }
                            </div>

                            {/* Total */}
                            <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                                <p className="text-sm font-medium text-amber-800">
                                    Montant total de l&apos;avoir : {' '}
                                    <span className="text-lg">
                                        {bulkRemovalModal.order.items
                                            ?.filter(item => getRemovableFromSelection(bulkRemovalModal.order).includes(item.imei))
                                            .reduce((sum, item) => sum + (item.price || 0), 0)
                                            .toFixed(2)
                                        }€
                                    </span>
                                </p>
                            </div>

                            {/* Reason selection */}
                            <div>
                                <Label className="text-sm font-medium">Raison du retrait *</Label>
                                <Select value={bulkRemovalReason} onValueChange={setBulkRemovalReason}>
                                    <SelectTrigger className="mt-1">
                                        <SelectValue placeholder="Sélectionner une raison..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {REMOVAL_REASONS.map(r => (
                                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Details for "other" reason */}
                            {bulkRemovalReason === 'other' && (
                                <div>
                                    <Label className="text-sm font-medium">Précisions</Label>
                                    <Textarea
                                        className="mt-1"
                                        placeholder="Précisez la raison..."
                                        value={bulkRemovalDetails}
                                        onChange={(e) => setBulkRemovalDetails(e.target.value)}
                                    />
                                </div>
                            )}

                            {/* Skip wallet credit option for unpaid orders */}
                            <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                <input
                                    type="checkbox"
                                    id="skipWalletCredit"
                                    checked={skipWalletCredit}
                                    onChange={(e) => setSkipWalletCredit(e.target.checked)}
                                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                                />
                                <label htmlFor="skipWalletCredit" className="text-sm">
                                    <span className="font-medium text-orange-800">Sans crédit Wallet</span>
                                    <span className="block text-xs text-orange-600">
                                        Cocher si la commande n'est pas encore totalement payée (virement en attente)
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter className="mt-4">
                        <Button
                            variant="outline"
                            onClick={() => setBulkRemovalModal({ open: false, order: null })}
                        >
                            Annuler
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleBulkRemoval}
                            disabled={removingBulk || !bulkRemovalReason}
                        >
                            {removingBulk ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            ) : (
                                <Trash2 className="w-4 h-4 mr-1" />
                            )}
                            Confirmer le retrait
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            </div>
        </AdminLayout>
    );
};

export default AdminOrderPreparation;
